import React, { useEffect, useState } from 'react'
import { SafeAreaView, StyleSheet } from 'react-native'

import { CoachReferralView } from 'partials/CoachRreferralView'
import { MyFormValues } from 'partials/EditProfileForm'
import { OnBoardingForm } from 'partials/OnBoarding/subcomponents/OnBoardingForm'
import { OnBoardingHeader } from 'partials/OnBoarding/subcomponents/OnBoardingHeader'

import { Box } from 'widgets/Box'
import { Image } from 'widgets/Image'
import { Text } from 'widgets/Text'

import { useCoachReferral } from 'hooks/useCoachReferral'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { useNavigateBackOrHome } from 'hooks/useNavigateBackOrHome'

import { analytics, logEvent } from 'utils/firebase'

import { useAuth as useAuthContext } from 'contexts/AuthContext'

import OnboardingImage03 from 'assets/images/onboarding-bio.png'
import OnboardingImage04 from 'assets/images/onboarding-location.png'
import OnboardingImage01 from 'assets/images/onboarding-name.png'
import OnboardingImage05 from 'assets/images/onboarding-profile-image.png'
import OnboardingImage02 from 'assets/images/onboarding-username.png'

import { Page } from '../Page'

type OnboardingViewProps = {
  initialValues?: MyFormValues
  onSubmit: (values: any, skipAvatar?: boolean) => void
}

const styles = StyleSheet.create({
  image: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  imageContainer: {
    maxHeight: 400,
  },
})

export const OnBoardingView = ({ initialValues, onSubmit }: OnboardingViewProps) => {
  const [initialData, setInitialData] = useState(initialValues)

  const coachReferral = useCoachReferral()
  const navigateBackOrHome = useNavigateBackOrHome()
  const maxWidth = useMaxWidth()
  const { firstName, lastName } = useAuthContext()

  const skipNameForm =
    (firstName !== undefined && firstName.length) || (lastName !== undefined && lastName.length)

  const totalSteps = skipNameForm ? 4 : 5
  const initialStep = skipNameForm ? 2 : 1
  const [currentStep, setCurrentStep] = useState(initialStep)

  const titles = [
    'Discover workouts, insights,\nand wellness creators',
    'Join Groups or\ncreate your own',
    'Post workouts or\nor insights to your feed',
    'Chat and compete\nwith your friends',
  ]

  const onBoardingImages = [
    OnboardingImage01,
    OnboardingImage02,
    OnboardingImage03,
    OnboardingImage04,
    OnboardingImage05,
  ]

  const logGAEvent = (action: 'next' | 'back' | 'skip' | 'finish') => {
    const pageTitlesForGA = [
      'first_last_name_page',
      'username_page',
      'bio_page',
      'address_page',
      'avatar_page',
    ]

    logEvent(analytics, 'on_boarding', {
      page_number: currentStep,
      page_title: pageTitlesForGA[currentStep - 1],
      action,
    })
  }

  useEffect(() => {
    if (!skipNameForm) return
    setInitialData({
      ...initialData,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipNameForm, firstName, lastName])

  const saveData = (values: any) => {
    setInitialData(values)
    handleOnNext()
  }

  const handleOnNext = (fireEvent?: boolean) => {
    if (currentStep < 5) {
      setCurrentStep((currentValue) => {
        return currentValue + 1
      })
      if (fireEvent === undefined || fireEvent) {
        logGAEvent('next')
      }
    }
  }

  const handleOnBack = () => {
    if (currentStep > initialStep) {
      setCurrentStep((currentValue) => {
        return currentValue - 1
      })
      logGAEvent('back')
    }
    if (currentStep === initialStep) {
      navigateBackOrHome()
      logGAEvent('back')
    }
  }

  const handleOnSkip = () => {
    switch (currentStep) {
      case 3: {
        setInitialData({
          ...initialData,
          bio: '',
        })
        handleOnNext(false)
        logGAEvent('skip')
        break
      }
      case 4: {
        setInitialData({
          ...initialData,
          city: '',
          state: '',
        })
        handleOnNext(false)
        logGAEvent('skip')
        break
      }
      case 5: {
        onSubmit(initialData, true)
        logGAEvent('skip')
        logGAEvent('finish')
        break
      }
      default:
        break
    }
  }

  return (
    <Page backgroundColor='black'>
      <SafeAreaView />
      <Box margin={{ horizontal: 'md' }}>
        <OnBoardingHeader
          currentStep={currentStep - (skipNameForm ? 1 : 0)}
          totalSteps={totalSteps}
        />
      </Box>

      {coachReferral.isReferral && currentStep !== 5 && <CoachReferralView data={coachReferral} />}

      {!coachReferral.isReferral || currentStep === 5 ? (
        <Box align='center' margin={{ horizontal: 'md' }}>
          {currentStep !== 5 && (
            <Box margin={{ vertical: 'smd' }}>
              <Text
                align='center'
                fontSize='lg'
                fontWeight='bold'
                color='white'
                bold
                numberOfLines={2}
              >
                {titles[currentStep - 1]}
              </Text>
            </Box>
          )}
          <Box
            margin={{ top: 'md' }}
            justifyContent='center'
            align='center'
            width='100%'
            height={maxWidth.width * 0.8}
            style={styles.imageContainer}
          >
            <Image
              cacheKey={`onBoardingStep-${currentStep}`}
              resizeMode='contain'
              style={styles.image}
              source={onBoardingImages[currentStep - 1]}
            />
          </Box>
        </Box>
      ) : null}

      <OnBoardingForm
        initialValues={initialData}
        pageIndex={currentStep}
        onSubmit={(values) => {
          onSubmit(values)
          logGAEvent('finish')
        }}
        saveData={saveData}
        onBack={handleOnBack}
        onSkip={handleOnSkip}
      />
    </Page>
  )
}
